<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Customers</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage your customer database</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="showCreateForm = true">
        Add Customer
      </v-btn>
    </div>

    <!-- Filters Card -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center">
        <v-text-field
          v-model="search"
          label="Search by name, last name, email, or phone"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 450px;"
          clearable
        ></v-text-field>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card>
      <v-data-table-server
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="customers"
        :items-length="total"
        :items-per-page-options="[20, 50, 100]"
        :loading="loading"
        :search="search"
        @update:options="loadItems"
    >
      <template #item.actions="{ item }">
          <div class="text-center">
            <v-menu>
              <template v-slot:activator="{ props }">
              <v-btn v-bind="props" density="compact" icon="mdi-dots-horizontal"></v-btn>
              </template>
              <v-list>
                <v-list-item @click="openQuotesModal(item)">
                  <v-list-item-title>View Quotes</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openEditForm(item)">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item >
                  <v-list-item-title @click="deleteCustomer(item.id)">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table-server>
      <div v-if="!customers.length && !loading" class="text-center py-8 text-medium-emphasis">
        No customers found.
      </div>
    </v-card>

    <!-- Create Customer Modal -->
    <v-dialog v-model="showCreateForm" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-account-plus</v-icon>
          Create Customer
        </v-card-title>
        <v-card-text>
          <CustomerForm mode="create" @submit="createCustomer" @cancel="showCreateForm = false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Customer Modal -->
    <v-dialog v-model="showEditForm" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-account-edit</v-icon>
          Edit Customer
        </v-card-title>
        <v-card-text>
          <CustomerForm :customer="editCustomerData" mode="edit" @submit="updateCustomer" @cancel="closeEditForm" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Customer Quotes Modal -->
    <CustomerQuotesModal
      :customer="selectedCustomerForQuotes"
      :show="showQuotesModal"
      @close="closeQuotesModal"
      @view-quote="viewQuoteDetails"
    />

    <!-- Customer Orders Modal -->
    <CustomerOrdersModal
      :customer="selectedCustomerForOrders"
      :show="showOrdersModal"
      @close="closeOrdersModal"
      @view-order="viewOrderDetails"
    />
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { customerService } from '../services';
import { useNotificationStore } from '../stores';
import { useDebounceSearch } from '../composables/useDebounceSearch';
import CustomerForm from '../components/CustomerForm.vue';
import CustomerQuotesModal from '../components/CustomerQuotesModal.vue';
import CustomerOrdersModal from '../components/CustomerOrdersModal.vue';

defineOptions({ name: 'CustomersView' });

const notificationStore = useNotificationStore();

const router = useRouter();

const customers = ref([]);
const search = ref('');
const page = ref(1);
const totalPages = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);
const loading = ref(false);

const showCreateForm = ref(false);
const showEditForm = ref(false);
const editCustomerData = ref(null);

const showQuotesModal = ref(false);
const selectedCustomerForQuotes = ref(null);

const showOrdersModal = ref(false);
const selectedCustomerForOrders = ref(null);

const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Last Name', key: 'lastName' },
  { title: 'Email', key: 'email' },
  { title: 'Phone', key: 'phone' },
  { title: 'City', key: 'city' },
  { title: 'Actions', key: 'actions', sortable: false },
];

async function loadCustomers(targetPage = 1, pageSize = 20) {
  loading.value = true;
  try {
    const data = await customerService.list({
      page: targetPage,
      limit: pageSize,
      search: search.value,
    });
    customers.value = data.data;
    total.value = data.total;
    totalPages.value = data.totalPages;
    page.value = data.page;
    itemsPerPage.value = pageSize;
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load customers');
  } finally {
    loading.value = false;
  }
}

useDebounceSearch(search, () => {
  page.value = 1;
  loadCustomers(1, itemsPerPage.value);
});

async function deleteCustomer(id) {
  if (!confirm('Are you sure you want to delete this customer?')) return;
  try {
    await customerService.delete(id);
    notificationStore.showSuccess('Customer deleted successfully');
    loadCustomers(page.value, itemsPerPage.value);
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to delete customer');
  }
}

function openEditForm(customer) {
  editCustomerData.value = { ...customer };
  showEditForm.value = true;
}

function closeEditForm() {
  showEditForm.value = false;
  editCustomerData.value = null;
}

async function createCustomer(formData) {
  try {
    await customerService.create(formData);
    showCreateForm.value = false;
    notificationStore.showSuccess('Customer created successfully');
    loadCustomers(page.value, itemsPerPage.value);
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to create customer');
  }
}

async function updateCustomer(formData) {
  try {
    await customerService.update(editCustomerData.value.id, formData);
    closeEditForm();
    notificationStore.showSuccess('Customer updated successfully');
    loadCustomers(page.value, itemsPerPage.value);
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to update customer');
  }
}

function openQuotesModal(customer) {
  selectedCustomerForQuotes.value = customer;
  showQuotesModal.value = true;
}

function closeQuotesModal() {
  showQuotesModal.value = false;
  selectedCustomerForQuotes.value = null;
}

function viewQuoteDetails(quote) {
  closeQuotesModal();
  router.push({ name: 'QuoteEntryPreview', params: { id: quote.id } });
}

function closeOrdersModal() {
  showOrdersModal.value = false;
  selectedCustomerForOrders.value = null;
}

function viewOrderDetails(order) {
  closeOrdersModal();
  router.push({ name: 'ImportedOrders' });
}

function loadItems(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  loadCustomers(page.value, itemsPerPage.value);
}

onMounted(() => {
  loadCustomers(page.value, itemsPerPage.value);
});
</script>

<style scoped>
:deep(.v-data-table) {
  border-radius: 0 0 12px 12px;
}

:deep(.v-data-table-header) {
  background-color: #f8fafc;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #64748b !important;
}
</style> 